import { Etasje } from '../../../../../../types/registration/userInput';
import { generateEtasjer } from '../../../../../../components/registering/steps/base/etasjeUtils';
import { JaNeiVetIkke } from '../../../../../../components/registering/utils/registerEnums';
import { OmBoligenFormData } from '../types';

const kjellerEtasjeDefault: Etasje = {
  id: crypto.randomUUID(),
  erKjeller: true,
  etasjeNummer: 1
};

// The content of this file is a hack to generate floors in the client. This should be moved to backend.

/**
 * Updates the floors (etasjer) array based on form data while preserving all detail data from existing floors.
 *
 * This function is called from `formDataToUserInput` in `dataMapper.ts` when:
 * - Creating a new registration from a previous one (copying registration data)
 * - Submitting the OmBoligen form to convert form data to UserInput format
 *
 * The function ensures that when floors are modified (added, removed, or basement status changes),
 * all detail data within existing floors is preserved, including:
 * - `bygningsForm` (building form)
 * - `gjennomsnittligEtasjehoyde` (average floor height)
 * - `vegger` (walls array with all wall details)
 * - `gulvSoner` (floor zones array with all floor details)
 *
 * @param existingEtasjer - The current floors array from the previous registration or existing userInput
 * @param hasHeatedBasement - Whether the current registration has a heated basement (from existing bygningsdetaljer)
 * @param formData - The form data from the OmBoligen form containing the new floor count and basement status
 * @returns Updated floors array with preserved detail data from existing floors and newly generated floors if needed
 *
 * @example
 * // When copying a registration with 3 floors and adding 1 more floor:
 * // Existing floors with detail data are preserved, and 1 new empty floor is added
 * const updated = updateEtasjer(existingFloors, true, { bygginfo: { floors: 4, isHeatedBasement: JaNeiVetIkke.Ja } });
 */
export const updateEtasjer = (existingEtasjer: Etasje[], hasHeatedBasement: boolean, formData: OmBoligenFormData) => {
  const newHasHeatedBasement = formData.bygginfo?.isHeatedBasement === JaNeiVetIkke.Ja;
  const newEtasjerCount = formData.bygginfo?.floors || 0;

  // Preserve existing floors with all its detail data
  const existingBasement = existingEtasjer.find((e) => e.erKjeller);
  const existingRegularFloors = existingEtasjer.filter((e) => !e.erKjeller);

  // Nothing has changed, early return
  if (newEtasjerCount === existingRegularFloors.length && newHasHeatedBasement === hasHeatedBasement) {
    return existingEtasjer;
  }

  const newEtasjer = updateRegularFloors(existingRegularFloors, newEtasjerCount);

  if (newHasHeatedBasement) {
    const basement = existingBasement || kjellerEtasjeDefault;
    return [basement, ...newEtasjer];
  }

  return newEtasjer;
};

const updateRegularFloors = (existingFloors: Etasje[], currentFloorCount: number): Etasje[] => {
  const existingFloorCount = existingFloors.length;

  // Remove excess etasjer whilst preserving detaildata from remaining floors
  if (existingFloorCount > currentFloorCount) {
    return existingFloors.slice(0, currentFloorCount);
  }

  if (existingFloorCount < currentFloorCount) {
    const newFloors = currentFloorCount - existingFloorCount;
    // generateEtasjer will add 1 to this, so floors will be numbered correctly (1, 2, 3...)
    return [...existingFloors, ...generateEtasjer(newFloors, existingFloorCount)];
  }

  // No change in floors, only in basement status (heated/ unheated)
  return [...existingFloors];
};
